import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from "tss-react/mui";
import ListItemIcon from '@mui/material/ListItemIcon';
import CategoryIcon from 'components/Icons/Category/Category';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import CompetenceIcon from 'components/Icons/Competency/Competency';
import List from '@mui/material/List';
import DatePicker from 'components/DatePicker';
import Grid from '@mui/material/Grid';
import ItemPicker from '../ItemPicker';
import { DateTime } from 'luxon';
import SelectionDialog from 'components/SelectionDialog';
import { Subtitle2 } from 'components/Typography';
import { expiryIds } from '../../domain';
import classNames from 'classnames';
import styles from './styles';
import repository from 'client/repository';
import LoadingPanel from 'components/LoadingPanel';

class AwardableCompetenceList extends React.Component {

    state = {
        categorySelectorOpen: false,
        competenceSelectorOpen: false,
        awardableCompetencies: [],
        selectedCategory: null,
        selectedCompetence: null,
        loadingCompetences: false,
        expiryDate: null
    };

    openCategoryDialog = () => {
        this.setState({ categorySelectorOpen: true });
    };

    closeCategoryDialog = () => {
        this.setState({ categorySelectorOpen: false });
    };

    openCompetenceDialog = () => {
        this.setState({ competenceSelectorOpen: true });
    };

    closeCompetenceDialog = () => {
        this.setState({ competenceSelectorOpen: false });
    };

    handleCategorySelected = async (category) => {

        this.setState({
            loadingCompetences: true,
            categorySelectorOpen: false
        });

        const { location, card } = this.props;
        let awardableCompetences = [];
        if (card && card.serialNumber && category && category.categoryID) {
            try {
                const response = await repository.supervisor.getAwardableCompetences(
                    card.serialNumber,
                    location.division.divisionID,
                    location.project.projectID,
                    location.site.siteID,
                    location.site.siteCode,
                    location.zone ? location.zone.zoneID : null,
                    location.zone ? location.zone.zoneCode : null,
                    category.categoryID
                );

                awardableCompetences = response.awardableComps;
            } catch (e) {
                console.log(e);
            }
        }

        this.setState({
            selectedCategory: category,
            categorySelectorOpen: false,
            selectedCompetence: null,
            expiryDate: null,
            loadingCompetences: false,
            awardableCompetencies: awardableCompetences
        });

        this.props.competencyClicked(null);
    };

    handleCompetenceSelected = (competence) => {
        this.setState({
            selectedCompetence: competence,
            competenceSelectorOpen: false
        });

        this.props.competencyClicked(competence);
    };

    showExpiryDate(competence) {
        if (!Boolean(competence)) {
            return false;
        }

        return competence.expiryID.toUpperCase() === expiryIds.SPECIFY_EXPIRY_DATE;
    }

    setExpiryDate = (date) => {
        this.setState({ expiryDate: date });

        const today = DateTime.local().startOf('day');

        if (date && date.isValid && date.diff(today).as('minutes') >= 0) {
            this.props.handleExpiryDateSet(date.toFormat('dd/MM/yyyy'));
        } else {
            this.props.handleExpiryDateSet('');
        }
    };

    render() {
        const { classes, divisionData } = this.props;
        const { 
            selectedCategory, 
            selectedCompetence, 
            awardableCompetencies, 
            loadingCompetences 
        } = this.state;

        const categories = divisionData.awardableCategories;
        const hasCategory = Boolean(selectedCategory);
        const hasCompetence = Boolean(selectedCompetence);
        const showExpiryField = this.showExpiryDate(selectedCompetence);

        return (
            <List>
                <SelectionDialog
                    open={this.state.categorySelectorOpen}
                    title="Select a category"
                    handleClose={this.closeCategoryDialog}
                    items={categories}
                    displayProp="categoryName"
                    itemIcon={
                        <ListItemIcon>
                            <CategoryIcon />
                        </ListItemIcon>
                    }
                    onItemSelected={this.handleCategorySelected} />

                <SelectionDialog
                    open={this.state.competenceSelectorOpen}
                    title="Select a competency"
                    handleClose={this.closeCompetenceDialog}
                    items={awardableCompetencies}
                    displayProp="name"
                    itemIcon={
                        <ListItemIcon>
                            <ThumbUpIcon />
                        </ListItemIcon>
                    }
                    onItemSelected={this.handleCompetenceSelected} />

                <ItemPicker
                    onSelectionChanged={this.openCategoryDialog}
                    selectedText={hasCategory ? selectedCategory.categoryName : ''}
                    selected={hasCategory}
                    pickerName="Category"
                    activeClass={classes.categoryActive}
                    disabled={false}
                    icon={<CategoryIcon />}
                />

                <div className={!hasCategory ? classes.disabled : ''}>
                    {loadingCompetences 
                        ? <LoadingPanel message="Loading competencies" />
                        : <ItemPicker
                                disabled={!hasCategory}
                                onSelectionChanged={this.openCompetenceDialog}
                                selectedText={hasCompetence ? selectedCompetence.name : ''}
                                selected={hasCompetence}
                                pickerName="Competency"
                                activeClass={classes.competenceActive}
                                icon={<CompetenceIcon />}
                            />
                    }                    
                </div>

                {showExpiryField &&
                    <Grid container
                        alignItems="center"
                        alignContent="flex-start"
                        justifyContent="flex-start"
                        spacing={2}
                        className={classNames(classes.expiryPanel, showExpiryField ? '' : classes.disabled)}>
                        <Grid item xs={12} className={classes.expiryDateLabel}>
                            <Subtitle2>Expiry Date:</Subtitle2>
                        </Grid>
                        <Grid item xs={12}>
                            <DatePicker
                                name="expiry"
                                style={{ marginTop: -12 }}
                                disabled={!showExpiryField}
                                disablePast
                                value={this.state.expiryDate}
                                onChange={this.setExpiryDate}
                                openTo="year"
                                views={["year", "month", "day"]}
                            />
                        </Grid>
                    </Grid>
                }
            </List>
        );
    }
}

AwardableCompetenceList.propTypes = {
    divisionData: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    card: PropTypes.object.isRequired
};
export default withStyles(AwardableCompetenceList,styles);